import type { DashboardTrendPoint } from "../trips/selectors";

type TrendKey = "active" | "notBilled" | "overdue" | "exceptions";

export function OperationalTrendChart({
  trend,
}: {
  trend: DashboardTrendPoint[];
}) {
  const width = 640;
  const height = 235;
  const left = 36;
  const right = 18;
  const top = 20;
  const bottom = 34;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const maxValue = Math.max(
    5,
    ...trend.flatMap((point) => trendSeries.map((item) => point[item.key]))
  );
  const xFor = (index: number) =>
    left + (plotWidth / Math.max(1, trend.length - 1)) * index;
  const yFor = (value: number) =>
    top + plotHeight - (value / maxValue) * plotHeight;

  return (
    <div className="min-w-0">
      <svg
        role="img"
        aria-label="Operational status trend for the last six months"
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((step) => {
          const y = top + plotHeight * step;
          return (
            <g key={step}>
              <line
                x1={left}
                x2={width - right}
                y1={y}
                y2={y}
                stroke="var(--orbit-hair)"
                strokeWidth="1"
              />
              <text
                x={left - 10}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fill="var(--orbit-muted)"
              >
                {Math.round(maxValue * (1 - step))}
              </text>
            </g>
          );
        })}
        {trend.map((point, index) => (
          <g key={point.monthKey}>
            <line
              x1={xFor(index)}
              x2={xFor(index)}
              y1={top}
              y2={top + plotHeight}
              stroke="var(--orbit-hair)"
              strokeWidth="1"
            />
            <text
              x={xFor(index)}
              y={height - 8}
              textAnchor="middle"
              fontSize="11"
              fill="var(--orbit-muted)"
            >
              {point.monthLabel}
            </text>
          </g>
        ))}
        {trendSeries.map((item) => (
          <g key={item.key}>
            <polyline
              fill="none"
              stroke={item.color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={trend
                .map((point, index) => `${xFor(index)},${yFor(point[item.key])}`)
                .join(" ")}
            />
            {trend.map((point, index) => (
              <circle
                key={`${item.key}-${point.monthKey}`}
                cx={xFor(index)}
                cy={yFor(point[item.key])}
                r="4"
                fill={item.color}
                stroke="var(--orbit-surface)"
                strokeWidth="2"
              />
            ))}
          </g>
        ))}
      </svg>
      <div className="mt-1 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs font-semibold text-slate-500">
        {trendSeries.map((item) => (
          <span key={item.key} className="inline-flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

const trendSeries: Array<{
  key: TrendKey;
  label: string;
  color: string;
}> = [
  { key: "active", label: "Active", color: "var(--orbit-periwinkle)" },
  { key: "notBilled", label: "Not Billed", color: "var(--orbit-green)" },
  { key: "overdue", label: "Overdue", color: "var(--orbit-gold)" },
  { key: "exceptions", label: "Exceptions", color: "var(--orbit-red)" },
];
